import { useTranslation } from 'react-i18next';

export interface Entity {
  id: string;
  title: string;
  type: string;
  lifecycle: string;
  customer?: string;
  supplier?: string;
  products?: string[];
  due_date?: string;
  updated_at: string;
  signal_count?: number;
}

interface EntityCardProps {
  entity: Entity;
  onViewDetail: (id: string) => void;
  compact?: boolean;
}

const lifecycleColors: Record<string, string> = {
  detected: 'bg-blue-500',
  confirmed: 'bg-green-500',
  scheduled: 'bg-indigo-500',
  in_production: 'bg-purple-500',
  ready: 'bg-emerald-500',
  delivered: 'bg-teal-500',
  completed: 'bg-gray-400',
  cancelled: 'bg-red-500',
};

const typeColors: Record<string, string> = {
  order: 'bg-blue-100 text-blue-700',
  delivery_update: 'bg-indigo-100 text-indigo-700',
  supplier_issue: 'bg-red-100 text-red-700',
  production_task: 'bg-purple-100 text-purple-700',
  payment_request: 'bg-emerald-100 text-emerald-700',
  general_followup: 'bg-gray-100 text-gray-600',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const mins = Math.floor((now - then) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function EntityCard({
  entity,
  onViewDetail,
  compact = false,
}: EntityCardProps) {
  const { t } = useTranslation();
  const dotColor = lifecycleColors[entity.lifecycle] ?? 'bg-gray-400';
  const typeBadge =
    typeColors[entity.type] ?? 'bg-gray-100 text-gray-600';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onViewDetail(entity.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onViewDetail(entity.id); }}
      className={`rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
        compact ? 'p-3' : 'p-4'
      }`}
    >
      {/* Type + lifecycle */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${typeBadge}`}
        >
          {t(`entities.types.${entity.type}`, entity.type)}
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
          <span className={`inline-block h-2 w-2 rounded-full ${dotColor}`} />
          {t(
            `entities.lifecycle.${entity.lifecycle}`,
            entity.lifecycle,
          )}
        </span>
        {entity.signal_count != null && entity.signal_count > 0 && (
          <span className="inline-flex items-center rounded-full bg-primary-100 text-primary-700 px-2 py-0.5 text-[10px] font-medium ltr:ml-auto rtl:mr-auto">
            {'📡'} {entity.signal_count}
          </span>
        )}
      </div>

      {/* Title */}
      <h4
        className={`font-semibold text-gray-900 leading-snug mb-1 ${
          compact ? 'text-sm line-clamp-1' : 'text-sm line-clamp-2'
        }`}
      >
        {entity.title}
      </h4>

      {/* Details */}
      {!compact && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-2">
          {entity.customer && (
            <span>{'👤'} {entity.customer}</span>
          )}
          {entity.supplier && (
            <span>{'🏭'} {entity.supplier}</span>
          )}
          {entity.products && entity.products.length > 0 && (
            <span className="truncate max-w-[180px]">
              {'📦'} {entity.products.join(', ')}
            </span>
          )}
          {entity.due_date && (
            <span>{'📅'} {formatDate(entity.due_date)}</span>
          )}
          <span className="ltr:ml-auto rtl:mr-auto">
            {relativeTime(entity.updated_at)}
          </span>
        </div>
      )}

      {/* View action — hidden in compact (sidebar) mode */}
      {!compact && (
        <div className="mt-3 flex justify-end">
          <button
            onClick={(e) => { e.stopPropagation(); onViewDetail(entity.id); }}
            className="text-xs text-primary-600 hover:text-primary-800 font-medium"
            aria-label={`View details for ${entity.title}`}
          >
            {t('entities.viewDetails', 'View details')} →
          </button>
        </div>
      )}
    </div>
  );
}
